import type { WaitingItem } from '../../services/waiting.service';

/**
 * What each kind of waiting work is called on the screen (#340). The server sends the kind's
 * key; the words live here so the Spanish dictionary reaches them. A key this map does not
 * know yet is shown as it comes, never dropped.
 */
export const WAITING_LABELS: Readonly<Record<string, string>> = {
  withdrawal_retention_call: 'Withdrawal requests waiting for the retention call',
  withdrawal_retention_late: 'Withdrawal requests past the retention call or with no agent',
  withdrawals_to_approve: 'Withdrawals to approve',
  deposits_to_approve: 'Deposits to approve',
  source_accounts_to_review: 'Source accounts to review',
  credits_to_attribute: 'Bank credits to attribute',
  commitment_cancellations: 'Commitment cancellations to decide',
  deposit_reversals_to_review: 'Deposit reversals to review',
  withdrawal_reversals_to_review: 'Withdrawal reversals to review',
  redemptions_to_approve: 'Redemptions to approve',
  payouts_to_submit: 'Payouts to send to the bank',
  payouts_to_confirm: 'Payouts to confirm as paid',
  payout_returns_to_confirm: 'Payout returns to confirm',
  bank_voids_to_confirm: 'Bank line voids to confirm',
  bank_debit_explanations_to_confirm: 'Bank debit explanations to confirm',
  bank_returns_to_settle: 'Bank returns to settle',
  return_destination_exceptions_to_decide: 'Return destination exceptions to decide',
  destination_exceptions_to_decide: 'Payout destination exceptions to decide',
  contract_reviews: 'Contract reviews',
  onboarding_reviews: 'Onboarding reviews',
  callbacks_for_a_manager: 'Callbacks escalated or with no agent',
  sanctions_false_positives_to_confirm: 'Sanctions false positives to confirm',
  alert_dismissals_to_confirm: 'Monitoring alert dismissals to confirm',
  risk_band_changes_to_decide: 'Risk band changes to decide',
  reactivations_to_approve: 'Account reactivations to approve',
  sars_to_decide: 'Suspicious activity reports to decide',
  deaths_to_confirm: 'Reported deaths to confirm',
  deceased_holdings_to_exit: 'Holdings of a deceased holder with no exit given',
  ofac_releases_to_confirm: 'Blocked property releases to confirm',
  sanctions_delistings_to_confirm: 'Sanctions delistings to confirm',
  ofac_reports_to_file: 'Reports to OFAC to file',
  suspicion_reports_to_decide: 'Suspicion reports to decide',
  transfer_cases_to_assess: 'Transfers from other accounts to assess',
  data_requests_open: 'Personal data requests open',
  accounts_to_confirm: 'Fund accounts to confirm',
  products_not_operable: 'Products not operable',
};

/** The kinds that have something waiting, in the server's order. */
export function waitingNow(items: readonly WaitingItem[]): WaitingItem[] {
  return items.filter((item) => item.count > 0);
}

/** The label of one kind; its key when this screen does not know it yet. */
export function waitingLabel(key: string): string {
  return WAITING_LABELS[key] ?? key;
}

/** The withdrawal kinds whose flag is the retention-call limit. */
const RETENTION_KINDS = new Set(['withdrawal_retention_call', 'withdrawal_retention_late']);

/** Whether a withdrawal request is among what is shown, so the limit is explained. */
export function showsRetentionLimit(items: readonly WaitingItem[]): boolean {
  return items.some((item) => RETENTION_KINDS.has(item.key) && item.count > 0);
}
